import React, { useCallback, useEffect, useRef } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/helper';
import {
  acknowledgeActivationCompletion,
  recordActivationCelebrationOutcome,
  selectActivationCompletedCount,
  selectActivationTrackerPhase,
  selectOwnerActivation,
  selectShouldPlayActivationConfetti,
} from '../../../redux/ownerActivationSlice';
import { selectValidCurrentLocation } from '../../../redux/locationSlice';
import ownerActivationAnalytics from '../../../services/ownerActivationAnalytics';
import type {
  ActivationTrackerProperties,
  OwnerActivationAnalyticsPlatform,
} from '../../../services/ownerActivationAnalyticsTypes';
import { GTFonts, useTheme } from '../../../theme';
import { fs, s, v } from '../../../utils/libs';

/**
 * Connected tracker-completion controller.
 *
 * Historically this rendered in-slot confetti when all five activation tasks
 * completed. That confetti has moved to the post-wizard Home landing modal
 * (WizardHomeCelebrationController) — a moment every new owner hits — since
 * finishing all five tasks is rare on day one and made the celebration
 * effectively invisible.
 *
 * Kept responsibilities:
 * - Persist a `played` outcome once, so the acknowledgement bar surfaces
 *   the moment tracker phase turns `completed_unacknowledged` for the
 *   current location, and so the tracker→Quick Actions restoration flow
 *   continues to work.
 * - Emit `activation_confetti_outcome` and `activation_all_completed`
 *   analytics — the funnel definitions in
 *   contracts/analytics.md still expect these markers.
 * - Render the acknowledgement bar with the "Show Quick Actions" affordance.
 */

const analyticsPlatform = (): OwnerActivationAnalyticsPlatform => {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return 'web';
};

const OwnerActivationCelebrationController: React.FC = () => {
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const shouldPlay = useAppSelector(selectShouldPlayActivationConfetti);
  const phase = useAppSelector(selectActivationTrackerPhase);
  const completedCount = useAppSelector(selectActivationCompletedCount);
  const owner = useAppSelector(selectOwnerActivation);
  const currentLocation = useAppSelector(selectValidCurrentLocation);
  const currentLocationId = currentLocation?._id ?? null;
  const businessType = useAppSelector(
    state => state.auth.inApp.account.businessInfo?.type,
  );

  const outcomeRecordedRef = useRef(false);

  // Reset one-shot guard when the active location changes, so a switch
  // to another real location that just hit "all complete" still records.
  useEffect(() => {
    outcomeRecordedRef.current = false;
  }, [currentLocationId]);

  const trackerProps = useCallback((): ActivationTrackerProperties => {
    const trackerPhase = phase === 'inactive' ? 'active' : phase;
    return {
      onboarding_version: 1,
      platform: analyticsPlatform(),
      business_type_group: businessType || undefined,
      tracker_phase: trackerPhase,
      completed_task_count: completedCount,
      wizard_product_created: owner.wizard.productCreatedDuringWizard,
    };
  }, [
    businessType,
    completedCount,
    owner.wizard.productCreatedDuringWizard,
    phase,
  ]);

  // Record the outcome synchronously once per location bucket. This gates
  // the acknowledgement bar without any particle timing dependency.
  useEffect(() => {
    if (!shouldPlay) return;
    if (!currentLocationId) return;
    if (outcomeRecordedRef.current) return;
    outcomeRecordedRef.current = true;

    dispatch(
      recordActivationCelebrationOutcome({
        locationId: currentLocationId,
        handledAt: new Date().toISOString(),
        outcome: 'played',
      }),
    );
    try {
      ownerActivationAnalytics.track('activation_confetti_outcome', {
        ...trackerProps(),
        confetti_outcome: 'played',
      });
      ownerActivationAnalytics.track(
        'activation_all_completed',
        trackerProps(),
      );
    } catch {
      // analytics must never break Home
    }
  }, [currentLocationId, dispatch, shouldPlay, trackerProps]);

  const onAcknowledge = useCallback(() => {
    if (!currentLocationId) return;
    const acknowledgedAt = new Date().toISOString();
    dispatch(
      acknowledgeActivationCompletion({
        locationId: currentLocationId,
        acknowledgedAt,
      }),
    );
    try {
      ownerActivationAnalytics.track(
        'activation_completion_acknowledged',
        trackerProps(),
      );
    } catch {
      // ignore
    }
  }, [currentLocationId, dispatch, trackerProps]);

  const showAck =
    phase === 'completed_unacknowledged' &&
    !!currentLocationId &&
    owner.tracker.byLocation[currentLocationId]?.celebrationHandledAt !== null;

  if (!showAck) return null;

  return (
    <View
      style={[styles.ackBar, { backgroundColor: theme.colors.surface }]}
      testID="activation-completion-acknowledge">
      <Text style={[styles.ackCopy, { color: theme.colors.text }]}>
        You've explored the BizPlus essentials
      </Text>
      <Text
        accessibilityRole="button"
        accessibilityLabel="Show Quick Actions"
        onPress={onAcknowledge}
        style={[styles.ackButton, { color: theme.colors.success }]}>
        Show Quick Actions
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  ackBar: {
    marginTop: v(8),
    paddingHorizontal: s(12),
    paddingVertical: v(12),
    borderRadius: 8,
    gap: v(8),
  },
  ackCopy: {
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    lineHeight: fs(18),
  },
  ackButton: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(14),
    lineHeight: fs(18),
  },
});

export default OwnerActivationCelebrationController;
